import {AlertIcon, StopIcon, XIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, IconButton, Link} from '@primer/react'
import {useState} from 'react'

import {useApproveWorkflows} from '../hooks/use-approve-workflows'
import type {PendingWorkflowApproval} from '../index'

import styles from './PendingWorkflowApprovalBanner.module.css'

interface PendingWorkflowApprovalBannerProps {
  pendingWorkflowApproval: PendingWorkflowApproval
  repositoryNwo: string
  onApproved?: () => void
}

export function PendingWorkflowApprovalBanner({
  pendingWorkflowApproval,
  repositoryNwo,
  onApproved,
}: PendingWorkflowApprovalBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false)

  const {
    workflowsRequiringApprovalCount,
    viewerCanApproveWorkflowRuns,
    hasExpiredWorkflowRuns,
    approvalRequiredMessage,
    helpLink,
  } = pendingWorkflowApproval

  const {isPending, error, approve} = useApproveWorkflows(
    repositoryNwo,
    pendingWorkflowApproval.pullRequestNumber,
    onApproved,
  )

  if (isDismissed) {
    return null
  }

  const title = `${workflowsRequiringApprovalCount} workflow${
    workflowsRequiringApprovalCount === 1 ? '' : 's'
  } awaiting approval`

  const subtitle = hasExpiredWorkflowRuns ? (
    'Unable to re-run one or more workflows because they were created over a month ago.'
  ) : (
    <>
      {approvalRequiredMessage}{' '}
      <Link inline href={helpLink}>
        Learn more about approving workflows.
      </Link>
    </>
  )

  return (
    <>
      <div className={styles.Banner}>
        <AlertIcon className={styles.WarningIcon} />
        <div className={styles.BannerContent}>
          <div className={styles.BannerTextContent}>
            <div className={styles.BannerTitle}>{title}</div>
            <div className={styles.BannerSubtitle}>{subtitle}</div>
          </div>
          <div className={styles.BannerActions}>
            {viewerCanApproveWorkflowRuns && (
              <Button loading={isPending} loadingAnnouncement="Approving workflows" onClick={approve}>
                Approve workflows to run
              </Button>
            )}
            <IconButton
              icon={XIcon}
              variant="invisible"
              aria-label="Dismiss workflow approval notice"
              onClick={() => setIsDismissed(true)}
              size="small"
            />
          </div>
        </div>
      </div>
      {error && (
        <Flash className={styles.ErrorFlash} variant="danger">
          <StopIcon className="mr-2" />
          {error}
        </Flash>
      )}
    </>
  )
}
